import type { WithT } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { Flight } from '@feathr/blackbox';
import { ModalV1, toast } from '@feathr/components';

import FlightPathSettings from './FlightPathSettings';

interface IProps {
  flight: Flight;
  toggleModal: () => void;
}

function onConfirm({ flight, t, toggleModal }: IProps & WithT) {
  return async () => {
    try {
      await flight.save();
      if (flight.collection) {
        flight.collection.refreshApiCache();
      }
      toast(t('Updated settings.'), { type: 'success' });
      toggleModal();
    } catch {
      toast(t("Couldn't save settings. Try again."), { type: 'error' });
    }
  };
}

function MakeSharedFlightModal({ flight, toggleModal }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const snapshot = useRef({
    shared: flight.get('shared'),
    shared_description: flight.get('shared_description'),
    shared_image: flight.get('shared_image'),
    shared_name: flight.get('shared_name'),
  });

  function handleClose(): void {
    flight.set(snapshot.current);
    toggleModal();
  }

  return (
    <ModalV1
      controlled={true}
      ephemeral={true}
      onClose={handleClose}
      onConfirm={onConfirm({ flight, t, toggleModal })}
      t={t}
      title={t('Make flight path')}
    >
      <FlightPathSettings flight={flight} />
    </ModalV1>
  );
}

export default observer(MakeSharedFlightModal);
